import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiArrowRight } from 'react-icons/fi';
import { GoogleMap, Marker } from '@react-google-maps/api';
import { useMaps } from './MapsProvider';
import CardRail, { railCardVariants } from './CardRail';

const ease = [0.22, 1, 0.36, 1];

const DestinationsSection = ({ handleDestinationBooking }) => {
  const { isLoaded, mapOptions } = useMaps();
  const [map, setMap] = useState(null);

  const destinationsData = [
    {
      name: "Kassiopi",
      slug: "kassiopi",
      description: "Our home base - picturesque fishing village with ancient castle ruins and crystal waters",
      image: "/images/destinations/kassiopi/istockphoto-1484583938-612x612.jpg",
      highlights: ["Castle Ruins", "Harbor Views", "Tavernas", "Water Sports"],
      transferTime: "Home Base",
      position: { lat: 39.7896, lng: 19.9139 },
      popular: true
    },
    {
      name: "Barbati Beach",
      slug: "barbati",
      description: "Stunning pebble beach with crystal-clear turquoise waters and dramatic mountain backdrop",
      image: "/images/destinations/barbati-beach/DJI-0314-barbati-beach-corfu-aerial-view-02.webp",
      highlights: ["Pebble Beach", "Clear Waters", "Beach Bars", "Mountain Views"],
      transferTime: "25 min",
      position: { lat: 39.71750, lng: 19.86710 }
    },
    {
      name: "Old Perithia",
      slug: "acharavi",
      description: "Ancient abandoned mountain village showcasing traditional Corfiot architecture and stunning panoramic views",
      image: "/images/destinations/old perithia/Old-Perithia-Village-by-The-Merchant-House-Hotel-19 (1).jpg",
      highlights: ["Historic Village", "Traditional Architecture", "Mountain Views", "Cultural Heritage"],
      transferTime: "35 min",
      position: { lat: 39.763604, lng: 19.875119 }
    },
    {
      name: "Canal d'Amour",
      slug: "sidari",
      description: "Famous heart-shaped cliff formation and golden sandy beaches in Sidari",
      image: "/images/destinations/canal-damour/caption.jpg",
      highlights: ["Heart-shaped Cliffs", "Golden Sand", "Romantic Views", "Photo Spots"],
      transferTime: "35 min",
      position: { lat: 39.7915, lng: 19.6798 }
    },
    {
      name: "Paleokastritsa",
      slug: "paleokastritsa",
      description: "Crystal-clear waters and dramatic cliffs make this the crown jewel of Corfu",
      image: "/images/destinations/paleokastritsa/caption.jpg",
      highlights: ["Azure Waters", "Monastery Visit", "Boat Tours", "Beach Clubs"],
      transferTime: "50 min",
      position: { lat: 39.6787, lng: 19.7034 }
    },
    {
      name: "Corfu Old Town",
      slug: "corfu-town",
      description: "UNESCO World Heritage site with Venetian architecture and charming streets",
      image: "/images/destinations/corfu-old-town/Old-Town-corfu-greece-00001.jpg",
      highlights: ["Historic Sites", "Private Tours", "Museums", "Local Cuisine"],
      transferTime: "55 min",
      position: { lat: 39.6243, lng: 19.9213 }
    }
  ];

  useEffect(() => {
    window.handleDestinationBooking = handleDestinationBooking;
    return () => {
      delete window.handleDestinationBooking;
    };
  }, [handleDestinationBooking]);

  return (
    <section id="destinations" className="relative py-24 lg:py-32 bg-white">

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease }}
          className="text-center mb-16"
        >
          <p className="text-[#ba1609] text-xs uppercase tracking-[0.4em] mb-4">Where We Take You</p>
          <h2 className="text-3xl lg:text-5xl font-light text-[#1a1012]" style={{ fontFamily: "'Playfair Display', serif" }}>
            Discover <span className="italic">Corfu</span>
          </h2>
        </motion.div>

        <CardRail label="destinations">
          {destinationsData.map((destination) => (
            <motion.div key={destination.slug} variants={railCardVariants}>
              <Link
                to={`/booking?dropoff_name=${encodeURIComponent(destination.name)}&dropoff_lat=${destination.position.lat}&dropoff_lng=${destination.position.lng}`}
                className="group block relative overflow-hidden rounded-2xl h-80"
              >
                {/* Background Image */}
                <div
                  className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110"
                  style={{
                    backgroundImage: `url('${destination.image}')`,
                  }}
                />
                {/* Scrim: the mid-band carries the description, so it cannot fade out too early
                    or the text drops under 3:1 over the bright photos (turquoise water, pale sand). */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/55 to-transparent" />

                {/* Badges */}
                <div className="absolute top-4 left-4 flex gap-2">
                  {destination.popular && (
                    <span className="text-[10px] bg-[#ba1609] text-white px-3 py-1 rounded-full uppercase tracking-wider border border-[#ba1609] backdrop-blur-sm">
                      Popular
                    </span>
                  )}
                  <span className="text-[10px] bg-black/50 text-white/90 px-3 py-1 rounded-full tracking-wider backdrop-blur-sm">
                    {destination.transferTime}
                  </span>
                </div>

                {/* Content */}
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="text-xl text-white font-medium mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {destination.name}
                  </h3>
                  <p className="text-white/80 text-sm font-light leading-relaxed mb-3 line-clamp-2">
                    {destination.description}
                  </p>
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {destination.highlights.map((h) => (
                      <span key={h} className="text-[10px] bg-white/10 border border-white/20 px-2 py-1 rounded text-white/85">
                        {h}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center justify-between pt-3 border-t border-white/20">
                    <span className="text-white text-sm font-light">Book Transfer</span>
                    <FiArrowRight className="w-4 h-4 text-white/70 group-hover:text-[#e23b2c] group-hover:translate-x-1 transition-all duration-300" />
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </CardRail>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease }}
          className="text-center mb-16"
        >
          <Link
            to="/airport-transfers"
            className="text-[#ba1609] hover:text-[#8e1107] hover:bg-[#fbeceb] px-8 py-4 rounded-full border border-[#ba1609]/30 hover:border-[#ba1609] transition-all duration-300 text-sm tracking-wide inline-block"
          >
            View All Destinations & Routes
          </Link>
        </motion.div>

        {/* Interactive Map */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease }}
        >
          <div>
            <div className="text-center mb-6">
              <h3 className="text-xl lg:text-2xl font-light text-[#ba1609] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                Interactive <span className="italic">Map</span>
              </h3>
              <p className="text-[#5b4d4f] text-sm font-light">
                Click on any location to see details and book your transfer
              </p>
            </div>

            {isLoaded ? (
              <div className="h-[500px] w-full rounded-xl overflow-hidden">
                <GoogleMap
                  mapContainerStyle={{ width: '100%', height: '100%' }}
                  center={{ lat: 39.7391, lng: 19.8227 }}
                  zoom={10.5}
                  options={{
                    ...mapOptions,
                    minZoom: 9,
                    maxZoom: 16,
                    mapTypeControl: false,
                    streetViewControl: false,
                    fullscreenControl: true,
                    gestureHandling: 'cooperative'
                  }}
                  onLoad={(mapInstance) => setMap(mapInstance)}
                >
                  {destinationsData.map((destination, index) => (
                    <Marker
                      key={index}
                      position={destination.position}
                      icon={{
                        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
                          <svg width="40" height="40" xmlns="http://www.w3.org/2000/svg">
                            <defs>
                              <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
                                <feDropShadow dx="2" dy="2" stdDeviation="3" flood-color="rgba(0,0,0,0.3)"/>
                              </filter>
                            </defs>
                            <circle cx="20" cy="20" r="16" fill="${destination.popular ? '#ba1609' : '#8e1107'}" stroke="#ffffff" stroke-width="3" filter="url(#shadow)"/>
                            <circle cx="20" cy="20" r="8" fill="#ffffff"/>
                            <text x="20" y="26" text-anchor="middle" font-family="Arial, sans-serif" font-size="10" font-weight="bold" fill="${destination.popular ? '#ba1609' : '#8e1107'}">
                              ${index + 1}
                            </text>
                          </svg>
                        `)}`,
                        scaledSize: new window.google.maps.Size(40, 40),
                        anchor: new window.google.maps.Point(20, 20)
                      }}
                      onClick={() => {
                        const infoWindowContent = `
                          <div style="max-width: 350px; font-family: system-ui, -apple-system, sans-serif; position: relative;">
                            <button onclick="if(window.currentInfoWindow) window.currentInfoWindow.close();"
                                    style="position: absolute; top: 8px; right: 8px; z-index: 10; width: 32px; height: 32px; border-radius: 50%; background: rgba(0,0,0,0.7); color: white; border: none; cursor: pointer; font-size: 18px; display: flex; align-items: center; justify-content: center; backdrop-filter: blur(4px); line-height: 1;">
                              ✕
                            </button>
                            <div style="position: relative; margin: -1rem; margin-bottom: 1rem;">
                              <img src="${destination.image}" alt="${destination.name}"
                                   style="width: 100%; height: 200px; object-fit: cover; border-radius: 8px 8px 0 0;"/>
                              ${destination.popular ? '<div style="position: absolute; top: 8px; left: 8px; background: #ba1609; color: white; padding: 4px 8px; border-radius: 12px; font-size: 10px; font-weight: 600;">Popular</div>' : ''}
                              <div style="position: absolute; bottom: 8px; left: 8px; background: rgba(0,0,0,0.7); color: white; padding: 4px 8px; border-radius: 8px; font-size: 12px; backdrop-filter: blur(4px);">
                                ${destination.transferTime} from Kassiopi
                              </div>
                            </div>
                            <h3 style="margin: 0 0 8px 0; font-size: 18px; font-weight: 600; color: #1f2937;">
                              ${destination.name}
                            </h3>
                            <p style="margin: 0 0 12px 0; color: #6b7280; font-size: 14px; line-height: 1.4;">
                              ${destination.description}
                            </p>
                            <a href="/booking?dropoff_name=${encodeURIComponent(destination.name)}&dropoff_lat=${destination.position.lat}&dropoff_lng=${destination.position.lng}"
                               style="display: block; width: 100%; padding: 12px; background: #ba1609;
                                      color: white; border: none; border-radius: 8px; font-weight: 600;
                                      cursor: pointer; font-size: 14px; text-align: center; text-decoration: none;">
                              Book Transfer
                            </a>
                          </div>
                        `;

                        if (window.currentInfoWindow) {
                          window.currentInfoWindow.close();
                        }

                        if (map) {
                          const infoWindow = new window.google.maps.InfoWindow({
                            content: infoWindowContent,
                            maxWidth: 380,
                            pixelOffset: new window.google.maps.Size(0, -10)
                          });

                          const marker = new window.google.maps.Marker({
                            position: destination.position,
                            map: map
                          });

                          infoWindow.open(map, marker);
                          window.currentInfoWindow = infoWindow;
                        }
                      }}
                    />
                  ))}
                </GoogleMap>
              </div>
            ) : (
              <div className="h-[500px] w-full bg-white rounded-xl flex items-center justify-center">
                <div className="text-center">
                  <div className="animate-spin h-12 w-12 border-4 border-[#ba1609] rounded-full border-t-transparent mx-auto mb-4" />
                  <p className="text-[#5b4d4f] font-light">Loading interactive map...</p>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default DestinationsSection;
